import { CLI_VERSION, cliDownloadUrl } from '@mogzo/shared';
import { CodeBlock } from '@mogzo/ui';
import { Link } from 'react-router';
import { Code, DocLink, DocTable, Lead } from '../parts';

const install = () => `npm install -g ${cliDownloadUrl(window.location.origin)}`;

const START_CARDS = [
  ['cloud-saves', 'Cloud saves', 'Each player’s progress, on every device.'],
  ['game-data', 'Game data', 'Levels, inventories and things players make.'],
  ['leaderboards', 'Leaderboards', 'Best scores, daily, weekly, monthly or for ever.'],
  ['achievements', 'Achievements', 'Unlocks, with MOGZO’s pop-up.'],
  ['remote-config', 'Remote config', 'Change settings without a new build.'],
  ['testing', 'Testing', 'Your private test community and its testers.'],
  ['submitting', 'Submitting and publishing', 'Checks, review, then going live.'],
  ['limits', 'Limits', 'Sizes, quotas and how calls are sent.'],
] as const;

export function Introduction() {
  return (
    <>
      <h1>MOGZO developer docs</h1>
      <Lead>
        Make a browser game for MOGZO, give it cloud saves, leaderboards and achievements, test it
        with your own testers, and send it for review. These docs cover the MOGZO CLI (version{' '}
        {CLI_VERSION}) and the SDK.
      </Lead>

      <h2 id="quick-start">Start in five minutes</h2>
      <p>
        You need Node.js 22 or newer. These install the CLI, sign this computer in, make a small
        working game, play it on your computer, then post it to your private test community.
      </p>
      <CodeBlock
        title="Terminal"
        plain
        code={`${install()}
mogzo login
mogzo create "Star Catcher"
cd star-catcher
mogzo dev
mogzo upload`}
      />
      <p>
        Each step is explained in <DocLink page="first-game">Your first game</DocLink>.
      </p>

      <h2 id="how-it-works">How MOGZO works</h2>
      <ul>
        <li>
          A <strong>project</strong> is one game. It has a private <strong>test community</strong>{' '}
          at <Code>mogzo.com/test/&lt;address&gt;</Code>, where your builds are posted for the
          testers you invite.
        </li>
        <li>
          Your game talks to MOGZO through the <DocLink page="sdk">SDK</DocLink>: who’s playing,
          their saves, game data, leaderboards, achievements and remote config.
        </li>
        <li>
          When it’s ready, <DocLink page="submitting">send it for review</DocLink>. Once it’s
          approved, you publish it.
        </li>
      </ul>

      <h2 id="topics">Where to go next</h2>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3">
        {START_CARDS.map(([page, title, text]) => (
          <Link
            key={page}
            to={`/docs/${page}`}
            className="mz-panel flex flex-col gap-1 p-4 no-underline hover:border-line-strong"
          >
            <span className="font-medium">{title}</span>
            <span className="text-[13px] text-muted">{text}</span>
          </Link>
        ))}
      </div>
    </>
  );
}

export function Install() {
  return (
    <>
      <h1>Install the CLI</h1>
      <Lead>
        The MOGZO CLI makes games, plays them on your computer, uploads them and sends them for
        review.
      </Lead>

      <h2 id="node">Node.js</h2>
      <p>
        You need <strong>Node.js 22 or newer</strong>. Check with <Code>node --version</Code>; if
        it’s missing or older, install the LTS version from nodejs.org.
      </p>

      <h2 id="install">Install</h2>
      <CodeBlock title="Terminal" plain code={install()} />
      <p>
        Check it worked with <Code>mogzo --version</Code>. If your terminal says the command isn’t
        found, see <DocLink page="troubleshooting">When something goes wrong</DocLink>.
      </p>

      <h2 id="update">Update</h2>
      <p>When a new version comes out, the CLI says so. Update it with:</p>
      <CodeBlock title="Terminal" plain code="mogzo update" />
      <p>
        New SDK features come with new CLI versions; after updating, give each game the newest SDK
        with <Code>mogzo sdk update</Code> (see <DocLink page="sdk">The SDK</DocLink>).
      </p>
    </>
  );
}

export function FirstGame() {
  return (
    <>
      <h1>Your first game</h1>
      <Lead>From an empty folder to a game your testers can play, in four commands.</Lead>

      <h2 id="sign-in">Sign in</h2>
      <CodeBlock title="Terminal" plain code="mogzo login" />
      <p>
        The CLI prints a link to <strong>dev.mogzo.com/cli</strong> and a short code. Open the link,
        check that the code on the page matches the one in your terminal, then press{' '}
        <strong>Approve</strong>. Only approve codes from a <Code>mogzo login</Code> you just ran
        yourself.
      </p>
      <p>
        The computer then appears under <strong>CLI sessions</strong>, where you can sign it out. A
        sign-in ends after 90 days without use, or when you change your password.
      </p>

      <h2 id="create">Make a game</h2>
      <CodeBlock title="Terminal" plain code={'mogzo create "Star Catcher"'} />
      <p>
        This makes the project on MOGZO and a folder, <Code>star-catcher</Code>, with a small
        working game in it: Orbit Hop, with a splash that plays itself, a weekly leaderboard and
        four achievements (made for you in the project’s <strong>Data</strong> tab). The game’s
        address on MOGZO comes from its name; choose another with <Code>--address</Code>. If you
        already made the project in the dashboard, use its address and the folder is linked to it.
      </p>
      <p>
        Templates: plain JavaScript (the default) or <Code>--template phaser</Code> for Phaser 3. Or
        start from your own game: add a <DocLink page="manifest">mogzo.json</DocLink> to its folder.
      </p>

      <h2 id="play">Play it on your computer</h2>
      <CodeBlock title="Terminal" plain code="mogzo dev" />
      <p>
        Opens your game at <Code>http://127.0.0.1:4321</Code>, inside a stand-in for the MOGZO game
        page: the same locked-down frame, and a test player so the SDK answers like it will on
        mogzo.com. With a splash, it opens on the splash, wide and tall as posts show it; its Play
        launches the game. Edit your files and reload the page. A log shows every message between
        the game and MOGZO.
      </p>
      <p>
        Options: <Code>--player "Alex"</Code> for the test player’s name, <Code>--guest</Code> to
        play signed out, <Code>--port</Code> if 4321 is taken.
      </p>

      <h2 id="upload">Upload</h2>
      <CodeBlock title="Terminal" plain code={'mogzo upload --notes "First try"'} />
      <p>
        Checks and packages the game, uploads it, and waits until MOGZO has unpacked it. It then
        prints your test community, <Code>mogzo.com/test/&lt;address&gt;</Code>, where the build is
        posted with the game’s splash, pinned on top for your testers. The build also appears on the
        project’s Overview, with any reason it was refused.
      </p>
      <p>
        To check without uploading: <Code>mogzo validate</Code>. To see exactly what would be
        uploaded: <Code>mogzo build</Code> writes it to <Code>.mogzo/build.zip</Code>.
      </p>

      <h2 id="next">Next</h2>
      <ul>
        <li>
          <DocLink page="testing">Invite testers</DocLink> to your test community.
        </li>
        <li>
          Add <DocLink page="cloud-saves">cloud saves</DocLink>,{' '}
          <DocLink page="leaderboards">leaderboards</DocLink> and{' '}
          <DocLink page="achievements">achievements</DocLink>.
        </li>
      </ul>
    </>
  );
}

export function Manifest() {
  return (
    <>
      <h1>mogzo.json</h1>
      <Lead>Every game folder has one, next to the game’s page. It describes the build.</Lead>

      <h2 id="example">An example</h2>
      <CodeBlock
        title="mogzo.json"
        code={`{
  "id": "star-catcher",
  "name": "Star Catcher",
  "version": "0.1.0",
  "entry": "index.html",
  "splash": "splash.html",
  "orientation": "landscape",
  "devices": ["desktop", "mobile"]
}`}
      />

      <h2 id="fields">The fields</h2>
      <DocTable
        head={['Field', 'What it is']}
        rows={[
          [
            <Code key="c">id</Code>,
            'The game’s address on MOGZO; it decides which project an upload goes to.',
          ],
          [<Code key="c">name</Code>, 'Optional. The game’s name comes from the dashboard.'],
          [
            <Code key="c">version</Code>,
            'Yours to raise, like 1.2.0; shown with each build. Each submission needs a higher one.',
          ],
          [<Code key="c">entry</Code>, 'The page that starts the game.'],
          [
            <Code key="c">splash</Code>,
            'Optional. The game’s splash page, shown in posts before anyone plays.',
          ],
          [
            <Code key="c">orientation</Code>,
            'landscape, portrait or any. Only where the store listing starts; once you save the listing, its value counts.',
          ],
          [
            <Code key="c">devices</Code>,
            'desktop, mobile or both. Like orientation, only a starting value for the store listing.',
          ],
          [
            <Code key="c">network.allow</Code>,
            'Other servers the game may reach. Anything else is refused at submission.',
          ],
          [
            <Code key="c">build</Code>,
            'For games with a build step: the command, and the folder it writes.',
          ],
        ]}
      />
      <p>
        Leaderboards and achievements aren’t set here: make them in your project’s{' '}
        <strong>Data</strong> tab, or from the CLI (see{' '}
        <DocLink page="ai-tools">AI coding tools</DocLink>).
      </p>

      <h2 id="build">Games with a build step</h2>
      <p>
        Games built with Vite, webpack, or exported from Unity or Godot say how to build and where
        the result is. <Code>mogzo build</Code> and <Code>mogzo upload</Code> run the command, then
        upload that folder; <Code>mogzo dev</Code> serves it.
      </p>
      <CodeBlock
        title="mogzo.json"
        code={`"build": { "command": "npm run build", "output": "dist" }`}
      />
    </>
  );
}

export function AiTools() {
  return (
    <>
      <h1>AI coding tools</h1>
      <Lead>
        You, or an AI coding tool such as Claude Code or Cursor, can make a game’s leaderboards and
        achievements and fill in its store listing from its folder with the CLI.
      </Lead>

      <h2 id="commands">Leaderboards and achievements from the CLI</h2>
      <CodeBlock
        title="Terminal"
        code={`mogzo leaderboards list
mogzo leaderboards create weekly-taps --name "Weekly taps" --highest-first --reset weekly --min 0
mogzo achievements create tap-100 --name "Tap 100 times" --points 25 --steps 100 --icon zap --color sky
mogzo achievements edit tap-100 --points 30
mogzo achievements icons            # the MOGZO icons and colours
mogzo achievements delete tap-100   # asks first; --yes to skip`}
      />
      <ul>
        <li>
          They show in your project’s <strong>Data</strong> tab tagged CLI, with the computer they
          came from, and you can change them there like any other.
        </li>
        <li>
          Changes go to test only, with the same rules as the dashboard. Live players get them when
          you publish a version.
        </li>
        <li>
          <Code>edit</Code> changes only the options given. Add <Code>--json</Code> for answers a
          tool can read; <DocLink page="cli">CLI commands</DocLink> lists every option.
        </li>
        <li>
          The tool works with your CLI sign-in. To stop a computer, sign it out under CLI sessions.
        </li>
      </ul>

      <h2 id="listing">The store listing from the CLI</h2>
      <p>
        <Code>mogzo listing pull</Code> writes the listing as files in <Code>listing/</Code>, a tool
        edits them, takes screenshots with <Code>mogzo screenshots</Code>, and{' '}
        <Code>mogzo listing push</Code> makes them the draft, marked CLI on the Store listing tab.
        It’s reviewed when you submit it, as always. See{' '}
        <DocLink page="store-listing" heading="from-the-cli">
          Store listing
        </DocLink>
        .
      </p>

      <h2 id="agents-md">AGENTS.md</h2>
      <p>
        Every game made with <Code>mogzo create</Code> has an <Code>AGENTS.md</Code>, and a{' '}
        <Code>CLAUDE.md</Code> that points to it, telling AI tools how to work on a MOGZO game: the
        SDK, <Code>mogzo.json</Code>, the store listing, the limits and the commands.{' '}
        <Code>mogzo sdk update</Code> adds them to older games, and adds new sections, such as the
        store listing’s, to an older <Code>AGENTS.md</Code> without changing the rest. The same
        notes are at <a href="/llms.txt">dev.mogzo.com/llms.txt</a>.
      </p>
    </>
  );
}
